import React, { useState } from 'react';
import { apiRequest } from '../config/api';

interface AddAircraftModalProps {
  onClose: () => void;
  onAdded?: () => void;
}

const AIRCRAFT_LABELS: Record<string, string> = {
  balloon: 'Ballon',
  glider: 'Segelflugzeug',
  plane: 'Motorflugzeug',
  helicopter: 'Hubschrauber',
};

const AddAircraftModal: React.FC<AddAircraftModalProps> = ({ onClose, onAdded }) => {
  const [aircraftOption, setAircraftOption] = useState<'with-sensor' | 'without-sensor'>('with-sensor');
  const [aircraftType, setAircraftType] = useState('');
  const [identification, setIdentification] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const isValid = aircraftType !== '' && identification.trim() !== '';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid || submitting) return;
    setSubmitting(true);
    setError('');
    try {
      const res = await apiRequest('/aircraft', {
        method: 'POST',
        body: JSON.stringify({
          identification: identification.trim(),
          type: aircraftType,
          withSensor: aircraftOption === 'with-sensor',
        }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Fehler beim Speichern');
      }
      const data = await res.json();
      const label = AIRCRAFT_LABELS[aircraftType] || aircraftType;
      setSuccess(
        `${label} "${data.aircraft.identification}" wurde hinzugefügt.` +
        (data.sensorOrder ? ' Eine Sensorbestellung wurde angelegt.' : '')
      );
      onAdded?.();
    } catch (err: any) {
      setError(err.message || 'Unbekannter Fehler');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1040,
        }}
        onClick={onClose}
      />
      {/* Modal */}
      <div
        style={{
          position: 'fixed', top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          background: 'white', borderRadius: '12px',
          padding: '32px', width: '100%', maxWidth: '480px',
          zIndex: 1050, boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
        }}
      >
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h5 style={{ fontWeight: 700, color: '#3d4554', margin: 0 }}>Weiteres Luftfahrzeug hinzufügen</h5>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#9aa0ad', lineHeight: 1 }}>×</button>
        </div>

        {success ? (
          <div>
            <div className="alert alert-success">{success}</div>
            <button className="btn btn-primary w-100" onClick={onClose}>Schließen</button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            {/* Sensor option */}
            <div className="mb-3">
              <div className="form-check mb-2">
                <input
                  type="radio" id="addWithSensor" className="form-check-input"
                  checked={aircraftOption === 'with-sensor'}
                  onChange={() => setAircraftOption('with-sensor')}
                />
                <label htmlFor="addWithSensor" className="form-check-label">
                  Flugobjekt anlegen und Sensor bestellen
                </label>
              </div>
              <div className="form-check">
                <input
                  type="radio" id="addWithoutSensor" className="form-check-input"
                  checked={aircraftOption === 'without-sensor'}
                  onChange={() => setAircraftOption('without-sensor')}
                />
                <label htmlFor="addWithoutSensor" className="form-check-label">
                  Flugobjekt anlegen, vorerst kein Sensor
                </label>
              </div>
            </div>

            {/* Type */}
            <div className="mb-3">
              <label className="form-label">Typ <span className="text-danger">*</span></label>
              <select
                className="form-select"
                value={aircraftType}
                onChange={e => setAircraftType(e.target.value)}
                required
              >
                <option value="">Bitte wählen</option>
                <option value="balloon">Ballon</option>
                <option value="glider">Segelflugzeug</option>
                <option value="plane">Motorflugzeug</option>
                <option value="helicopter">Hubschrauber</option>
              </select>
            </div>

            {/* Identifier */}
            <div className="mb-4">
              <label className="form-label">Kennung <span className="text-danger">*</span></label>
              <input
                type="text"
                className="form-control"
                value={identification}
                onChange={e => setIdentification(e.target.value)}
                placeholder="z.B. D-ABCD"
                required
              />
            </div>

            {/* Sensor kit info */}
            {aircraftOption === 'with-sensor' && (
              <div className="mb-4 p-3 rounded" style={{ background: '#f4f6f9', fontSize: '13px', color: '#687088' }}>
                <strong>Sensorbestellung:</strong> Das Sensorkit enthält eine gedruckte Montageanleitung,
                die in Ihr Flughandbuch aufgenommen werden muss.
                Die Bestellung wird nach dem Hinzufügen als offene Bestellung für das Team sichtbar.
              </div>
            )}

            {error && <div className="alert alert-danger py-2 mb-3">{error}</div>}

            <div className="d-flex gap-2">
              <button type="button" className="btn btn-outline-secondary flex-fill" onClick={onClose}>
                Abbrechen
              </button>
              <button
                type="submit"
                className="btn btn-primary flex-fill"
                disabled={!isValid || submitting}
                style={{ fontWeight: 700 }}
              >
                {submitting ? 'Wird gespeichert…' : 'Hinzufügen'}
              </button>
            </div>
          </form>
        )}
      </div>
    </>
  );
};

export default AddAircraftModal;
